import React, { FC, Fragment, useState } from "react";
import { ButtonGroup, Form, ToggleButton } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faPen,
  faX,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import MyModal from "../Common/MyModal";
import MyCollapse from "../Common/MyCollapse";
import InputArgumentField from "../Builder/Components/sidebar/ProjectStartForm/InputArgumentField";
import SolutionImportInput from "../Solutions/SolutionImportInput";
import MonitoringConfigure from "../Monitoring/MonitoringConfigure";
import MonitoringDetails from "../Monitoring/MonitoringDetails";
import WeatherAlertConfiguration from "../Monitoring/WeatherAlertConfiguration";
import WeatherAlertCondition from "../Monitoring/WeatherAlertCondition";
import SelectApiConnection from "../Monitoring/SelectApiConnection";
import InboundSelector from "../Inbounds/InboundSelector";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import VisibilityInputs from "../Builder/Components/modals/VisibilityInputs";
import { pageProps } from "../helpers/pageProps";
import DeferredEditForm from "./DeferredEditForm";
import SolutionAiSuggestionMarker from "./SolutionAiSuggestionMarker";

interface SolutionStepInputGroupProps {
  inputGroup: any;
  groupIndex?: number;
  step: any;
  setStep: (step: any) => void;
  disableInputEditing?: boolean;
  disableInputDragging?: boolean;
  formData: Record<string, any>;
  setFormData?: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  onGroupDragHandlePointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  draggingInput?: any;
  overInput?: any;
  onMoveInput?: (
    sourceGroupId: string,
    sourceIndex: number,
    destGroupId: string,
    destIndex: number
  ) => void;
  onStartInputDrag: (
    e: React.PointerEvent<HTMLElement>,
    groupId: string,
    inputIndex: number,
    input: any,
    itemRect: DOMRect
  ) => void;
  sidebarDragging?: boolean;
  sidebarOverInput?: any;
  importContext?: any;
  usedInputs?: any[];
  inputUsageByKey?: Record<string, any[]>;
  onUsageSelect?: (usage: any) => void;
  getIsVisible?: (vg: any) => boolean;
  showDefaultValueInputsOnly?: boolean;
  onInputFocus?: (inputGroupId: string, input?: any) => void;
  onInputBlur?: (inputGroupId?: string, input?: any) => void;
  focusedInputNodeKey?: string | null;
  inputIndexFilter?: number;
  hideInputGroupHeader?: boolean;
  aiAssistantSuggestions?: any[];
  isAiAssistantLoading?: boolean;
  onAiAssistantSuggestionClick?: (suggestion: any) => void;
  onApplyAiAssistantSuggestion?: (suggestion: any) => void;
  onRemoveAiAssistantSuggestion?: (suggestion: any) => void;
}

const normalizeInputUsageTokens = (tokens: any[]) => {
  return Array.from(
    new Set(tokens.filter(Boolean).map((token) => String(token).toLowerCase()))
  );
};

const getInputUsageReferenceTokens = (ref: any) => {
  const componentUuid =
    ref?.ComponentUuid || ref?.SolutionComponentUuid || ref?.componentUuid;
  const field =
    ref?.Field || ref?.field || ref?.fieldKey || ref?.Uuid || ref?.UUID;

  return [
    ref?.UUID,
    ref?.Uuid,
    ref?.Value,
    ref?.Field,
    ref?.field,
    ref?.fieldKey,
    ref?.Name,
    componentUuid && field ? `${componentUuid}.${field}` : null,
    componentUuid && field ? `${componentUuid}:${field}` : null,
  ];
};

const getInputUsageTokens = (input: any) => {
  const mergedRefTokens = (input?.MergedInputRefs || []).flatMap(
    getInputUsageReferenceTokens
  );

  return normalizeInputUsageTokens([
    ...getInputUsageReferenceTokens(input),
    ...mergedRefTokens,
  ]);
};

const getInputUsageScopedReferenceTokens = (ref: any) => {
  const componentUuid =
    ref?.ComponentUuid || ref?.SolutionComponentUuid || ref?.componentUuid;
  const field =
    ref?.Field || ref?.field || ref?.fieldKey || ref?.Uuid || ref?.UUID;

  return [
    ref?.UUID,
    componentUuid && field ? `${componentUuid}.${field}` : null,
    componentUuid && field ? `${componentUuid}:${field}` : null,
  ];
};

const getInputUsageLookupTokens = (input: any) => {
  const hasComponentIdentity = Boolean(
    input?.ComponentUuid ||
      input?.SolutionComponentUuid ||
      input?.componentUuid ||
      input?.MergedInputRefs?.some(
        (ref: any) =>
          ref?.ComponentUuid || ref?.SolutionComponentUuid || ref?.componentUuid
      )
  );

  if (!hasComponentIdentity) {
    return getInputUsageTokens(input);
  }

  const mergedRefTokens = (input?.MergedInputRefs || []).flatMap(
    getInputUsageScopedReferenceTokens
  );

  return normalizeInputUsageTokens([
    ...getInputUsageScopedReferenceTokens(input),
    ...mergedRefTokens,
  ]);
};

const isInteractiveInputTarget = (target: HTMLElement) =>
  !!target.closest(
    "button, a, input, textarea, select, label, [role='button'], [contenteditable='true'], [data-token-editor], [data-select-input], .DraftEditor-root, .react-select__control, .react-select__menu, .react-select__option"
  );

// ── ComponentConfig Editor ───────────────────────────────────────────────────
function ComponentConfigEditor({
  input,
  importContext,
}: {
  input: any;
  importContext: any;
}) {
  const { configType, componentUuid } = input;
  const {
    monitors,
    setMonitors,
    inboundAPIs,
    setInboundAPIs,
    inboundCampaigns,
    setInboundCampaigns,
  } = importContext;

  if (
    (configType === "weather-monitor" || configType === "ping-monitor") &&
    monitors?.[componentUuid]
  ) {
    const monitor = monitors[componentUuid];
    const setMonitor = (m: any) =>
      setMonitors((prev: any) => ({ ...prev, [componentUuid]: m }));

    return (
      <div className="mt-2">
        <div className="mb-3">
          <div className="fw-bold mb-1">Monitor Priority</div>
          <ButtonGroup>
            {(["low", "medium", "high"] as const).map((p) => (
              <ToggleButton
                key={p}
                value={p}
                id={`${componentUuid}-priority-${p}`}
                type="radio"
                variant=""
                name={`${componentUuid}-priority`}
                className={`border ${
                  monitor.Priority === p ? "btn-colored" : "btn-light"
                }`}
                checked={monitor.Priority === p}
                onChange={() => setMonitor({ ...monitor, Priority: p })}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </div>
        {configType === "ping-monitor" && (
          <>
            <MonitoringConfigure
              monitor={monitor}
              setMonitor={setMonitor}
              backendErrors={undefined}
              setSelectedApiDetails={(api: any) =>
                setMonitor({ ...monitor, selectedApiDetails: api })
              }
              method={monitor.Method || "selectAnApi"}
              setMethod={(newMethod: any) => {
                if (newMethod === "createANewApi") {
                  setMonitor({
                    ...monitor,
                    Method: newMethod,
                    ApiConnectionUuid: null,
                    ApiConnection: {
                      Status: "production",
                      Label: componentUuid + " API Connection",
                      Kind: "third-party",
                      Profile: {
                        BaseUrl: "http://api:9090",
                        AuthHeader: "Authorization",
                        AuthValue: "Basic <base64encryptedstring>",
                        AuthKind: "",
                        Timeout: 1,
                        DefaultMethod: "GET",
                        ContentType: "JSON",
                      },
                    },
                  });
                } else {
                  setMonitor({
                    ...monitor,
                    ApiConnectionUuid: null,
                    ApiConnection: null,
                    Method: newMethod,
                  });
                }
              }}
            />
            <MonitoringDetails
              monitor={monitor}
              setMonitor={setMonitor}
              selectedApiDetails={monitor.selectedApiDetails}
            />
          </>
        )}
        {configType === "weather-monitor" && (
          <>
            <WeatherAlertConfiguration
              weatherAlertData={monitor.WeatherAlert}
              setWeatherAlertData={(WeatherAlert: any) =>
                setMonitor({ ...monitor, WeatherAlert })
              }
              errors={undefined}
            />
            <WeatherAlertCondition
              weatherAlertData={monitor.WeatherAlert}
              setWeatherAlertData={(WeatherAlert: any) =>
                setMonitor({ ...monitor, WeatherAlert })
              }
              errors={undefined}
            />
          </>
        )}
      </div>
    );
  }

  if (configType === "inbound-api") {
    return (
      <div className="mt-2">
        {!inboundAPIs?.[componentUuid] && (
          <div className="alert alert-info text-small">
            No API connection selected. A new one will be created automatically.
          </div>
        )}
        <SelectApiConnection
          apiKind="logic"
          selected={inboundAPIs?.[componentUuid]}
          onSelect={(api: any) =>
            setInboundAPIs((prev: any) => ({
              ...prev,
              [componentUuid]: api?.Uuid,
            }))
          }
        />
      </div>
    );
  }

  if (configType === "inbound-message") {
    return (
      <div className="mt-2">
        <InboundSelector
          inboundCampaign={inboundCampaigns?.[componentUuid] || {}}
          setInboundCampaign={(inboundCampaign: any) =>
            setInboundCampaigns((prev: any) => ({
              ...prev,
              [componentUuid]: inboundCampaign,
            }))
          }
        />
        <Input
          label="Keywords to match (Optional)"
          value={inboundCampaigns?.[componentUuid]?.Keywords || []}
          onChange={(keywords: any[]) =>
            setInboundCampaigns((prev: any) => ({
              ...prev,
              [componentUuid]: { ...prev[componentUuid], Keywords: keywords },
            }))
          }
          isMulti
          options={undefined}
          isValidNewOption={(v: string) => !!v.trim()}
        />
      </div>
    );
  }

  return null;
}

// ── ComponentConfig Input (summary + modal trigger) ─────────────────────────
function ComponentConfigInput({
  input,
  importContext,
}: {
  input: any;
  importContext: any;
}) {
  return (
    <div>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        <MyModal
          as="offcanvas"
          size="xxl"
          heading={`Configure ${input.Label}`}
          label={<FontAwesomeIcon className="text-secondary" icon={faPen} />}
          buttonClassName="btn-sm"
          style={{ width: 1000 }}
        >
          <ComponentConfigEditor input={input} importContext={importContext} />
        </MyModal>
      </div>

      {(() => {
        if (input.configType === "inbound-api") {
          const api = importContext?.inboundAPIs?.[input.ComponentUuid];

          return (
            <div className="alert alert-info text-small">
              {api ? (
                <div>
                  This input is linked to an Inbound API. The selected API's
                  details will be used when configuring the monitor, but you can
                  change the API connection before that.
                  <div>
                    <strong>Selected API:</strong> {api}
                  </div>
                </div>
              ) : (
                <div>
                  No API connection selected. A new one will be created
                  automatically
                </div>
              )}
            </div>
          );
        }

        if (input.configType === "inbound-message") {
          const campaign =
            importContext?.inboundCampaigns?.[input.ComponentUuid];
          const keywords = Array.isArray(campaign?.Keywords)
            ? campaign.Keywords
            : [];

          if (!campaign) {
            return (
              <div className="alert alert-info text-small mb-0">
                No inbound campaign configuration available.
              </div>
            );
          }

          return (
            <table className="table table-sm table-bordered mb-0">
              <tbody>
                <tr>
                  <th style={{ width: 180 }}>Channel</th>
                  <td>{campaign.Channel || "-"}</td>
                </tr>
                <tr>
                  <th>Inbound</th>
                  <td>
                    {campaign.Inbound ? (
                      campaign.Inbound + "@" + pageProps.emailSuffix.split("@")[1]
                    ) : (
                      <div className="text-danger text-small">
                        Inbound channel is required before deploy.
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Keywords</th>
                  <td>
                    {keywords.length > 0
                      ? keywords.map((k: string) => (
                          <span
                            key={k}
                            className="badge bg-colored rounded-pill me-1"
                          >
                            {k}
                          </span>
                        ))
                      : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          );
        }

        if (input.configType === "weather-monitor") {
          const monitor = importContext?.monitors?.[input.ComponentUuid];
          if (!monitor) {
            return (
              <div className="alert alert-info text-small mb-0">
                No monitor configuration available.
              </div>
            );
          }

          const weatherAlert = monitor?.WeatherAlert || {};
          const weatherConditions = Array.isArray(weatherAlert?.Conditions)
            ? weatherAlert.Conditions
            : [];

          return (
            <div className="d-flex flex-column gap-2">
              <div className="text-secondary text-small fw-bold text-uppercase">
                Weather Monitor Summary
              </div>
              <table className="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th style={{ width: 180 }}>Kind</th>
                    <td>{monitor.Kind || "-"}</td>
                  </tr>
                  <tr>
                    <th>Priority</th>
                    <td>{monitor.Priority || "-"}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>{monitor.Status || "-"}</td>
                  </tr>
                  <tr>
                    <th>Country ISO</th>
                    <td>{weatherAlert.CountryIso || "-"}</td>
                  </tr>
                  <tr>
                    <th>Geo Unit Type</th>
                    <td>{weatherAlert.GeoUnitType || "-"}</td>
                  </tr>
                  <tr>
                    <th>Geo Unit Group</th>
                    <td>{weatherAlert.GeoUnitGroup || "-"}</td>
                  </tr>
                  <tr>
                    <th>Shape Id</th>
                    <td>{weatherAlert.ShapeId ?? "-"}</td>
                  </tr>
                  <tr>
                    <th>Group Name</th>
                    <td>{weatherAlert.GroupName || "-"}</td>
                  </tr>
                  <tr>
                    <th>Alert Description</th>
                    <td>{weatherAlert.Description || "-"}</td>
                  </tr>
                  <tr>
                    <th>Conditions Count</th>
                    <td>{weatherConditions.length}</td>
                  </tr>
                </tbody>
              </table>

              {weatherConditions.length > 0 && (
                <table className="table table-sm table-striped table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Days Ahead</th>
                      <th>Color</th>
                      <th>Rules</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weatherConditions.map((condition: any, idx: number) => {
                      const daysAhead = Array.isArray(condition?.DaysAhead)
                        ? condition.DaysAhead.join(", ")
                        : "-";
                      const ruleCount = Array.isArray(
                        condition?.ConditionDetails
                      )
                        ? condition.ConditionDetails.length
                        : 0;
                      const orderValue =
                        condition?.Order !== undefined &&
                        condition?.Order !== null
                          ? String(condition.Order)
                          : String(idx);

                      return (
                        <tr key={`${orderValue}-${idx}`}>
                          <td>{idx + 1}</td>
                          <td>{condition?.Description || "-"}</td>
                          <td>{daysAhead || "-"}</td>
                          <td>{condition?.Color || "-"}</td>
                          <td>{ruleCount}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        }

        if (input.configType === "ping-monitor") {
          const monitor = importContext?.monitors?.[input.ComponentUuid];
          if (!monitor) {
            return (
              <div className="alert alert-info text-small mb-0">
                No monitor configuration available.
              </div>
            );
          }

          const isSelectAnApiMethod = monitor?.Method === "selectAnApi";
          const monitorPings = Array.isArray(monitor?.MonitorPings)
            ? monitor.MonitorPings
            : [];

          return (
            <div className="d-flex flex-column gap-2">
              <div className="text-secondary text-small fw-bold text-uppercase">
                Ping Monitor Summary
              </div>
              <table className="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th style={{ width: 180 }}>Kind</th>
                    <td>{monitor.Kind || "-"}</td>
                  </tr>
                  <tr>
                    <th>Priority</th>
                    <td>{monitor.Priority || "-"}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>{monitor.Status || "-"}</td>
                  </tr>
                  <tr>
                    <th>Method</th>
                    <td>{monitor.Method || "-"}</td>
                  </tr>
                  <tr>
                    <th>
                      {isSelectAnApiMethod
                        ? "API Connection UUID"
                        : "API Label"}
                    </th>
                    <td>
                      {isSelectAnApiMethod
                        ? monitor.ApiConnectionUuid || "-"
                        : monitor.ApiConnection?.Label || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Base URL</th>
                    <td>
                      {isSelectAnApiMethod
                        ? "N/A (uses selected API)"
                        : monitor.ApiConnection?.Profile?.BaseUrl || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Ping Count</th>
                    <td>{monitorPings.length}</td>
                  </tr>
                </tbody>
              </table>

              {monitorPings.length > 0 && (
                <table className="table table-sm table-striped table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>Ping Kind</th>
                      <th>Active</th>
                      <th>Path</th>
                      <th>Frequency</th>
                      <th>Timeout</th>
                      <th>Thresholds</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monitorPings.map((ping: any, pingIndex: number) => (
                      <tr
                        key={
                          ping.Uuid ||
                          `${ping.PingKind || "ping"}-${pingIndex + 1}`
                        }
                      >
                        <td>{ping.PingKind || "-"}</td>
                        <td>{ping.IsActive ? "Yes" : "No"}</td>
                        <td>{ping.Path || "-"}</td>
                        <td>
                          {`${ping.IntervalValue ?? "-"} ${
                            ping.IntervalUnit || ""
                          }`.trim()}
                        </td>
                        <td>{ping.Timeout ?? "-"}s</td>
                        <td>{`S:${ping.SuccessThreshold ?? "-"} / F:${
                          ping.FailureThreshold ?? "-"
                        }`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        }
      })()}
    </div>
  );
}

// Drag state (draggingInput, overInput, onStartInputDrag) is owned by
// SolutionStep and passed down so ALL groups share the same drag context.
const SolutionStepInputGroup: FC<SolutionStepInputGroupProps> = ({
  inputGroup,
  step,
  setStep,
  disableInputEditing = false,
  disableInputDragging = false,
  formData,
  setFormData,
  onGroupDragHandlePointerDown,
  // lifted input-drag props:
  draggingInput, // { groupId, index, uuid, label } | null
  overInput, // { groupId, index } | null
  onStartInputDrag, // (e, groupId, inputIndex, input, itemRect) => void
  sidebarDragging = false,
  sidebarOverInput = null,
  importContext = undefined,
  usedInputs = undefined,
  inputUsageByKey = undefined,
  onUsageSelect = undefined,
  getIsVisible = undefined,
  showDefaultValueInputsOnly = false,
  onInputFocus = undefined,
  onInputBlur = undefined,
  focusedInputNodeKey = null,
  inputIndexFilter = undefined,
  hideInputGroupHeader = false,
  aiAssistantSuggestions = [],
  isAiAssistantLoading = false,
  onAiAssistantSuggestionClick = undefined,
  onApplyAiAssistantSuggestion = undefined,
  onRemoveAiAssistantSuggestion = undefined,
}) => {
  const [focusedInputUuid, setFocusedInputUuid] = useState<string | null>(null);
  const disableStructureEditing = !!importContext?.disableStructureEditing;
  const resolvedShowDefaultValueInputsOnly =
    showDefaultValueInputsOnly || !!importContext?.showDefaultValueInputsOnly;

  const getRefValues = (ref: any) => {
    if (!ref?.ComponentUuid || !ref?.Field) return undefined;
    return formData?.[ref.ComponentUuid]?.[ref.Field];
  };

  const resolveInputValues = (input: any) => {
    const ownValues = formData?.[input.ComponentUuid]?.[input.Field];
    if (ownValues !== undefined) return ownValues;

    const mergedRefs = input?.MergedInputRefs || [];
    for (const ref of mergedRefs) {
      const refValues = getRefValues(ref);
      if (refValues !== undefined) return refValues;
    }

    return [];
  };

  const setMergedValues = (input: any, newValues: any) => {
    setFormData?.((prev) => {
      const next = { ...prev };
      if (input?.ComponentUuid && input?.Field) {
        next[input.ComponentUuid] = {
          ...(next[input.ComponentUuid] ?? {}),
          [input.Field]: newValues,
        };
      }

      const mergedRefs = input?.MergedInputRefs || [];
      mergedRefs.forEach((ref: any) => {
        if (!ref?.ComponentUuid || !ref?.Field) return;
        next[ref.ComponentUuid] = {
          ...(next[ref.ComponentUuid] ?? {}),
          [ref.Field]: newValues,
        };
      });

      return next;
    });
  };

  const updateInputGroup = (updatedGroup: any) => {
    setStep({
      ...step,
      inputGroups: step.inputGroups.map((g: any) =>
        g.id === updatedGroup.id ? updatedGroup : g
      ),
    });
  };

  const removeInputGroup = () => {
    setStep({
      ...step,
      inputGroups: step.inputGroups.filter((g: any) => g.id !== inputGroup.id),
    });
  };

  const updateInput = (updatedInput: any) => {
    updateInputGroup({
      ...inputGroup,
      inputs: inputGroup.inputs.map((i: any) =>
        i.UUID === updatedInput.UUID ? updatedInput : i
      ),
    });
  };

  const removeInput = (uuid: string) => {
    updateInputGroup({
      ...inputGroup,
      inputs: inputGroup.inputs.filter((i: any) => i.UUID !== uuid),
    });
  };

  const getInputFocusKey = (input: any, index: number) => {
    const identity =
      input?.UUID ||
      (input?.ComponentUuid && input?.Field
        ? `${input.ComponentUuid}:${input.Field}`
        : null) ||
      `${index}`;
    return `group:${inputGroup.id}|input:${identity}`;
  };

  const getParentInputFocusKey = (input: any, index: number) => {
    const identity =
      input?.UUID ||
      (input?.ComponentUuid && input?.Field
        ? `${input.ComponentUuid}:${input.Field}`
        : null) ||
      (input?.ComponentUuid && input?.Name
        ? `${input.ComponentUuid}:${input.Name}`
        : null) ||
      (input?.ComponentUuid && input?.Label
        ? `${input.ComponentUuid}:${input.Label}`
        : null) ||
      `${index}`;
    return `input:${identity}`;
  };

  const focusFirstFieldInCard = (cardEl: HTMLElement) => {
    const field = cardEl.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"], [aria-autocomplete], [contenteditable="true"]'
    );
    field?.focus();
  };

  const isAnyInputDragging = !!draggingInput || sidebarDragging;
  // Empty-zone highlight: only when no inputs exist and nothing better to show
  const emptyZoneIsTarget =
    isAnyInputDragging &&
    (overInput?.groupId === inputGroup.id ||
      sidebarOverInput?.groupId === inputGroup.id) &&
    inputGroup.inputs.length === 0;

  return (
    <div className="my-3 bg-white p-3 rounded-3 w-100">
      {/* Input group header */}
      {!hideInputGroupHeader && (
        <>
          <div className="text-secondary text-uppercase text-sm fw-bold mb-2 d-flex align-items-center justify-content-between edit-buttons">
            <div className="d-flex align-items-center gap-2">
              {!disableStructureEditing && (
                <span
                  onPointerDown={(e) => {
                    onGroupDragHandlePointerDown(e);
                  }}
                  className="text-secondary"
                  style={{ cursor: "grab" }}
                >
                  <FontAwesomeIcon icon={faGripVertical} />
                </span>
              )}
              {inputGroup.name}
            </div>
            <div>
              {!disableStructureEditing && (
                <MyModal
                  as="offcanvas"
                  heading={`Edit ${inputGroup.name}`}
                  label={
                    <FontAwesomeIcon className="text-secondary" icon={faPen} />
                  }
                  buttonClassName="btn-sm"
                >
                  {({ handleClose }) => (
                    <DeferredEditForm
                      value={inputGroup}
                      onCommit={updateInputGroup}
                      onClose={handleClose}
                    >
                      {(draftInputGroup, setDraftInputGroup) => (
                        <>
                          <div className="mb-3">
                            <label className="form-label">Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={draftInputGroup.name}
                              onChange={(e) =>
                                setDraftInputGroup({
                                  ...draftInputGroup,
                                  name: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description</label>
                            <input
                              type="text"
                              className="form-control"
                              value={draftInputGroup.description || ""}
                              onChange={(e) =>
                                setDraftInputGroup({
                                  ...draftInputGroup,
                                  description: e.target.value,
                                })
                              }
                            />
                          </div>
                          <VisibilityInputs
                            editedVariable={draftInputGroup}
                            setEditedVariable={setDraftInputGroup}
                            inputArguments={usedInputs as any}
                          />
                        </>
                      )}
                    </DeferredEditForm>
                  )}
                </MyModal>
              )}
              {!disableStructureEditing && (
                <button
                  className="btn btn-link p-0 ms-2 btn-sm"
                  onClick={removeInputGroup}
                >
                  <FontAwesomeIcon className="text-secondary" icon={faX} />
                </button>
              )}
            </div>
          </div>

          <div>{inputGroup.description}</div>
        </>
      )}

      {/* Inputs list */}
      <div
        className="d-flex flex-column gap-3 w-100"
        data-input-zone={inputGroup.id}
        data-input-count={inputGroup.inputs.length}
        style={{
          minHeight: 60,
          outline: emptyZoneIsTarget ? "2px dashed #6c5ce7" : "none",
          borderRadius: 8,
          transition: "outline 0.1s",
        }}
      >
        {inputGroup.inputs.map((input: any, index: number) => {
          if (
            typeof inputIndexFilter === "number" &&
            index !== inputIndexFilter
          ) {
            return null;
          }

          if (
            input?.MergedIntoRef ||
            (disableInputDragging && getIsVisible && !getIsVisible(input))
          )
            return null;

          if (
            resolvedShowDefaultValueInputsOnly &&
            (input.__kind === "external" || input.__kind === "component-config")
          ) {
            return null;
          }

          const mergedCount = Array.isArray(input?.MergedInputRefs)
            ? input.MergedInputRefs.length
            : 0;
          const mergedInputInfo =
            mergedCount > 0
              ? `Linked to ${mergedCount} input${
                  mergedCount > 1 ? "s" : ""
                }: ${input.MergedInputRefs.map(
                  (ref: any) => `${ref.ComponentUuid}.${ref.Field}`
                ).join(", ")}`
              : "";
          const inputUsages = Array.from(
            new Map(
              getInputUsageLookupTokens(input)
                .flatMap((token) => inputUsageByKey?.[token] || [])
                .map((usage: any) => [usage.key, usage])
            ).values()
          );

          const isDraggingThis =
            draggingInput?.uuid === input.UUID &&
            draggingInput?.groupId === inputGroup.id;
          const inputFocusKey = getInputFocusKey(input, index);
          const parentFocusSuffix = getParentInputFocusKey(input, index);
          const isFocusedByParent = !!(
            focusedInputNodeKey &&
            focusedInputNodeKey.includes(`|group:${inputGroup.id}|`) &&
            focusedInputNodeKey.endsWith(parentFocusSuffix)
          );
          const isFocusedThis =
            focusedInputUuid === inputFocusKey || isFocusedByParent;
          const inputSuggestions = aiAssistantSuggestions.filter(
            (suggestion: any) => {
              const change = suggestion.action?.change;
              return Number(change?.inputIndex) === index;
            }
          );

          const isMergeTargetThis =
            (draggingInput &&
              overInput?.groupId === inputGroup.id &&
              (overInput?.targetIndex === index ||
                overInput?.mergeCandidateIndex === index)) ||
            (sidebarDragging &&
              sidebarOverInput?.groupId === inputGroup.id &&
              (sidebarOverInput?.targetIndex === index ||
                sidebarOverInput?.mergeCandidateIndex === index));

          const activeInsertIndex =
            draggingInput && overInput?.groupId === inputGroup.id
              ? overInput?.mode === "merge"
                ? null
                : overInput.index
              : sidebarDragging && sidebarOverInput?.groupId === inputGroup.id
              ? sidebarOverInput?.mode === "merge"
                ? null
                : sidebarOverInput.index
              : null;

          const showTopLine =
            isAnyInputDragging &&
            !isDraggingThis &&
            !isMergeTargetThis &&
            activeInsertIndex === index;
          const showBottomLine =
            isAnyInputDragging &&
            !isDraggingThis &&
            !isMergeTargetThis &&
            index === inputGroup.inputs.length - 1 &&
            activeInsertIndex === inputGroup.inputs.length;

          return (
            <Fragment key={input.UUID + "|" + input.ComponentUuid}>
              <div
                data-input-item={index}
                data-group-id={inputGroup.id}
                data-input-target={`${inputGroup.id}:${index}`}
                className="border rounded-3 p-3 w-100 position-relative"
                tabIndex={0}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (isInteractiveInputTarget(target)) {
                    return;
                  }
                  const focusInput = { ...input, __focusIndex: index };
                  setFocusedInputUuid(inputFocusKey);
                  onInputFocus?.(inputGroup.id, focusInput);
                  focusFirstFieldInCard(e.currentTarget);
                }}
                onPointerDownCapture={(e) => {
                  const target = e.target as HTMLElement;
                  if (isInteractiveInputTarget(target)) {
                    return;
                  }

                  const focusInput = { ...input, __focusIndex: index };
                  setFocusedInputUuid(inputFocusKey);
                  onInputFocus?.(inputGroup.id, focusInput);
                }}
                onFocusCapture={() => {
                  const focusInput = { ...input, __focusIndex: index };
                  setFocusedInputUuid(inputFocusKey);
                  onInputFocus?.(inputGroup.id, focusInput);
                }}
                onBlurCapture={(e) => {
                  const nextTarget = e.relatedTarget as Node | null;
                  if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
                    const focusInput = { ...input, __focusIndex: index };
                    setFocusedInputUuid((prev) =>
                      prev === inputFocusKey ? null : prev
                    );
                    onInputBlur?.(inputGroup.id, focusInput);
                  }
                }}
                style={{
                  minWidth: 250,
                  opacity: isDraggingThis ? 0.35 : 1,
                  borderRadius: 8,
                  outline: isMergeTargetThis
                    ? "2px solid #16a34a"
                    : isFocusedThis
                    ? "2px solid #0d6efd"
                    : undefined,
                  outlineOffset: 0,
                  background: isMergeTargetThis ? "#f0fdf4" : undefined,
                  boxShadow: isMergeTargetThis
                    ? "0 0 0 2px rgba(22,163,74,0.18)"
                    : isFocusedThis
                    ? "0 0 0 2px rgba(13,110,253,0.2)"
                    : undefined,
                }}
              >
                <SolutionAiSuggestionMarker
                  suggestions={inputSuggestions}
                  isLoading={isAiAssistantLoading}
                  onSuggestionClick={onAiAssistantSuggestionClick}
                  onApplySuggestion={onApplyAiAssistantSuggestion}
                  onRemoveSuggestion={onRemoveAiAssistantSuggestion}
                />
                {showTopLine && (
                  <div
                    style={{
                      position: "absolute",
                      top: -5,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: "#6c5ce7",
                      borderRadius: 2,
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                )}
                {showBottomLine && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: -5,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: "#6c5ce7",
                      borderRadius: 2,
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  />
                )}
                <div className="d-flex justify-content-between align-items-center edit-buttons">
                  <div className="d-flex align-items-center gap-2 text-sm mb-1">
                    {!disableInputDragging && (
                      <span
                        onPointerDown={(e) => {
                          const itemEl =
                            e.currentTarget.closest("[data-input-item]");
                          const rect = itemEl?.getBoundingClientRect();
                          if (rect)
                            onStartInputDrag(
                              e,
                              inputGroup.id,
                              index,
                              input,
                              rect
                            );
                        }}
                        className="text-secondary"
                        style={{ cursor: "grab" }}
                      >
                        <FontAwesomeIcon icon={faGripVertical} />
                      </span>
                    )}
                    {input.Label}
                    {mergedCount > 0 && (
                      <span
                        className="badge bg-success-subtle text-success-emphasis border border-success-subtle"
                        title={mergedInputInfo}
                        aria-label={mergedInputInfo}
                      >
                        Merged +{mergedCount}
                      </span>
                    )}
                  </div>
                  <div>
                    {!disableStructureEditing && (
                      <>
                        <MyModal
                          as="offcanvas"
                          heading={`Edit ${input.Label}`}
                          label={
                            <FontAwesomeIcon
                              className="text-secondary"
                              icon={faPen}
                            />
                          }
                          buttonClassName="btn-sm"
                        >
                          {({ handleClose }) => (
                            <DeferredEditForm
                              value={input}
                              onCommit={updateInput}
                              onClose={handleClose}
                            >
                              {(draftInput, setDraftInput) => (
                                <>
                                  <div className="mb-3">
                                    <label className="form-label">Label</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={draftInput.Label}
                                      onChange={(e) =>
                                        setDraftInput({
                                          ...draftInput,
                                          Label: e.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Description
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={draftInput.Description}
                                      onChange={(e) =>
                                        setDraftInput({
                                          ...draftInput,
                                          Description: e.target.value,
                                        })
                                      }
                                    />
                                  </div>
                                  <VisibilityInputs
                                    editedVariable={draftInput}
                                    setEditedVariable={setDraftInput}
                                    inputArguments={usedInputs as any}
                                  />
                                </>
                              )}
                            </DeferredEditForm>
                          )}
                        </MyModal>
                        <button
                          className="btn btn-sm"
                          onClick={() => removeInput(input.UUID)}
                        >
                          <FontAwesomeIcon
                            className="text-secondary"
                            icon={faX}
                          />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="text-secondary text-sm mb-1">
                  {input.Description}
                </div>
                <fieldset
                  disabled={disableInputEditing}
                  className="border-0 p-0 m-0"
                >
                  {input.__kind === "external" && importContext ? (
                    <SolutionImportInput
                      fieldType={input.fieldType}
                      fieldKey={input.fieldKey}
                      value={
                        importContext.importedJSON?.Bag?.External?.[
                          input.fieldType
                        ]?.[input.fieldKey]
                      }
                      importedJSON={importContext.importedJSON}
                      setImportedJSON={importContext.setImportedJSON}
                      manualFields={importContext.manualFields}
                      setManualFields={importContext.setManualFields}
                      automaticallySelectedFields={
                        importContext.automaticallySelectedFields
                      }
                      setAutomaticallySelectedFields={
                        importContext.setAutomaticallySelectedFields
                      }
                      contactSets={importContext.contactSets}
                      reportingTopics={importContext.reportingTopics}
                      apiProfiles={importContext.apiProfiles}
                      setApiProfiles={importContext.setApiProfiles}
                      updateApiConnection={importContext.updateApiConnection}
                    />
                  ) : input.__kind === "component-config" && importContext ? (
                    <ComponentConfigInput
                      input={input}
                      importContext={importContext}
                    />
                  ) : (
                    <InputArgumentField
                      arg={input}
                      values={resolveInputValues(input)}
                      onChange={(newValues: any) =>
                        setMergedValues(input, newValues)
                      }
                      inputArgumentsInEditor={[]}
                      useOffcanvasForContactGroupModal
                    />
                  )}
                </fieldset>
                {inputUsages.length > 0 && (
                  <div className="mt-2">
                    <MyCollapse
                      heading={`This variable is used in ${
                        inputUsages.length
                      } place${inputUsages.length === 1 ? "" : "s"}`}
                      title={undefined}
                      headingActions={undefined}
                      actions={undefined}
                      show={undefined}
                      onShowChange={undefined}
                    >
                      {inputUsages.map((usage: any) => (
                        <div
                          key={usage.key}
                          className="d-flex align-items-start gap-2 text-sm mb-1"
                        >
                          <button
                            type="button"
                            className="btn btn-sm btn-light border py-0 px-1 flex-shrink-0"
                            title={`Show ${usage.kind.toLowerCase()} in flow`}
                            aria-label={`Show ${usage.kind.toLowerCase()} in flow`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              onUsageSelect?.(usage);
                            }}
                          >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          </button>
                          <div>
                            <span className="fw-bold">
                              {usage.componentLabel}
                            </span>{" "}
                            component
                            {usage.workflowLabel && (
                              <>
                                {" "}
                                in{" "}
                                <span className="fw-bold">
                                  {usage.workflowLabel}
                                </span>
                              </>
                            )}
                            {usage.eventLabel && (
                              <>
                                {" "}
                                when Event:{" "}
                                <span className="fw-bold">
                                  {usage.eventLabel}
                                </span>
                              </>
                            )}
                            {usage.ruleLabel && (
                              <>
                                {" "}
                                in{" "}
                                <span className="fw-bold">
                                  {usage.ruleLabel}
                                </span>
                              </>
                            )}{" "}
                            uses this variable in{" "}
                            <span className="fw-bold">
                              {usage.kind}: {usage.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </MyCollapse>
                  </div>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default SolutionStepInputGroup;
